import { useState, useRef, useEffect } from 'react';
import { OrganizationMember, getUserIdentifier } from '../lib/organizations';
import { RoleBadge } from './RoleBadge';
import { memberDisplayName, memberSearchName } from '../lib/members';

interface MemberSelectProps {
  members: OrganizationMember[];
  value: string;
  onChange: (memberId: string) => void;
  placeholder?: string;
  showInviteOption?: boolean;
  onInviteClick?: () => void;
  /** 검색 입력 필드 표시 여부 */
  searchable?: boolean;
  /** 비활성화 여부 */
  disabled?: boolean;
}

/**
 * 멤버 선택 드롭다운 컴포넌트
 * 배지 스타일로 권한 표시
 */
export function MemberSelect({
  members,
  value,
  onChange,
  placeholder = '선택하세요',
  showInviteOption = false,
  onInviteClick,
  searchable = false,
  disabled = false,
}: MemberSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // 선택된 멤버
  const selectedMember = members.find((m) => m.id === value);

  // 검색 필터링
  const filteredMembers = searchable && searchQuery.trim()
    ? members.filter((m) => {
        const query = searchQuery.toLowerCase();
        // 이름 정본은 Member — user만 보면 로그인 계정 없는 사람이 검색에 안 걸린다.
        // 자리표시('-')가 아니라 빈 문자열로 떨어져야 '-' 입력이 전원 매칭되지 않는다.
        const name = memberSearchName(m).toLowerCase();
        const identifier = getUserIdentifier(m.user?.auths ?? []).toLowerCase();
        return name.includes(query) || identifier.includes(query);
      })
    : members;

  // 드롭다운 열릴 때 검색 포커스
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen, searchable]);

  // 외부 클릭 감지
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (memberId: string) => {
    onChange(memberId);
    setIsOpen(false);
  };

  const handleInviteClick = () => {
    setIsOpen(false);
    onInviteClick?.();
  };

  return (
    <div ref={containerRef} className="relative">
      {/* 선택 버튼 */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`relative w-full bg-white border border-ink-300 rounded-control shadow-sm pl-3 pr-10 py-2 text-left focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 ${
          disabled ? 'bg-ink-100 cursor-not-allowed opacity-60' : 'cursor-pointer'
        }`}
      >
        {selectedMember ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 truncate">
              <span className="text-sm text-ink-900">{memberDisplayName(selectedMember)}</span>
              <span className="text-xs text-ink-500">
                ({getUserIdentifier(selectedMember.user?.auths ?? [])})
              </span>
            </div>
            <RoleBadge member={selectedMember} />
          </div>
        ) : (
          <span className="text-sm text-ink-500">{placeholder}</span>
        )}
        {/* 화살표 아이콘 */}
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg
            className={`h-5 w-5 text-ink-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>

      {/* 드롭다운 목록 */}
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-card py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none">
          {/* 검색 필드 */}
          {searchable && (
            <div className="px-2 pb-1 pt-1 sticky top-0 bg-white">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="이름 또는 이메일 검색..."
                className="w-full px-2 py-1.5 text-sm border border-ink-300 rounded-control focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {filteredMembers.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-500">
              {searchQuery.trim() ? '검색 결과가 없습니다' : '선택 가능한 멤버가 없습니다'}
            </div>
          ) : (
            filteredMembers.map((member) => (
              <button
                key={member.id}
                type="button"
                onClick={() => handleSelect(member.id)}
                className={`w-full text-left px-3 py-2 hover:bg-ink-100 ${
                  value === member.id ? 'bg-brand-50' : ''
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-ink-900">{memberDisplayName(member)}</span>
                    <span className="text-xs text-ink-500">
                      {getUserIdentifier(member.user?.auths ?? [])}
                    </span>
                  </div>
                  <RoleBadge member={member} />
                </div>
              </button>
            ))
          )}

          {/* 새 사용자 초대 옵션 */}
          {showInviteOption && (
            <>
              <div className="border-t border-ink-200 my-1" />
              <button
                type="button"
                onClick={handleInviteClick}
                className="w-full text-left px-3 py-2 text-sm text-brand-600 hover:bg-brand-50"
              >
                + 새 사용자 초대
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default MemberSelect;
